/**
 * Normalize permissions from API (object map or string[]) to string[] for UI checkboxes.
 */
export function permissionsToArray(
  permissions: string[] | Record<string, boolean> | null | undefined
): string[] {
  if (!permissions) return [];
  if (Array.isArray(permissions)) {
    return permissions.filter((p) => typeof p === 'string' && p.length > 0);
  }
  if (typeof permissions === 'object') {
    return Object.entries(permissions)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => key);
  }
  return [];
}

/** Firm-admin capabilities — never grant to non-admin roles even if stored in JSON. */
export const ADMIN_ONLY_PERMISSIONS = new Set([
  'users:read',
  'users:write',
  'users:delete',
  'settings:read',
  'settings:write',
  'audit:read',
  'reports:write',
]);

/**
 * Mirror of backend role ceilings (User.defaultPermissions).
 * Used when stored permissions are empty/stale so FE matches BE hasPermission.
 */
export const ROLE_DEFAULT_PERMISSIONS: Record<string, Record<string, boolean>> = {
  admin: {
    'users:read': true,
    'users:write': true,
    'users:delete': true,
    'cases:read': true,
    'cases:write': true,
    'cases:delete': true,
    'cases:assign': true,
    'clients:read': true,
    'clients:write': true,
    'clients:delete': true,
    'documents:read': true,
    'documents:write': true,
    'documents:delete': true,
    'reports:read': true,
    'reports:write': true,
    'audit:read': true,
    'settings:read': true,
    'settings:write': true,
    'tasks:read': true,
    'tasks:write': true,
    'calendar:read': true,
    'calendar:write': true,
    'calendar:delete': true,
    'time_entries:read': true,
    'time_entries:write': true,
    'time_entries:delete': true,
    'billing:read': true,
    'billing:write': true,
    'billing:delete': true,
    'trust:write': true,
    'portal:admin': true,
    'meetings:read': true,
    'meetings:write': true,
    'calls:read': true,
    'calls:write': true,
    'emails:read': true,
    'emails:write': true,
    'privilegeLog:read': true,
    'privilegeLog:write': true,
  },
  lawyer: {
    'cases:read': true,
    'cases:write': true,
    'cases:assign': true,
    'clients:read': true,
    'clients:write': true,
    'documents:read': true,
    'documents:write': true,
    'reports:read': true,
    'tasks:read': true,
    'tasks:write': true,
    'calendar:read': true,
    'calendar:write': true,
    'calendar:delete': true,
    'time_entries:read': true,
    'time_entries:write': true,
    'time_entries:delete': true,
    'billing:read': true,
    'billing:write': true,
    'trust:write': true,
    'portal:admin': true,
    'meetings:read': true,
    'meetings:write': true,
    'calls:read': true,
    'calls:write': true,
    'emails:read': true,
    'emails:write': true,
    'privilegeLog:read': true,
    'privilegeLog:write': true,
  },
  paralegal: {
    'cases:read': true,
    'cases:write': true,
    'clients:read': true,
    'clients:write': true,
    'documents:read': true,
    'documents:write': true,
    'reports:read': true,
    'tasks:read': true,
    'tasks:write': true,
    'calendar:read': true,
    'calendar:write': true,
    'time_entries:read': true,
    'time_entries:write': true,
    'billing:read': true,
    'meetings:read': true,
    'meetings:write': true,
    'calls:read': true,
    'calls:write': true,
    'emails:read': true,
    'emails:write': true,
  },
  assistant: {
    'cases:read': true,
    'cases:write': true,
    'clients:read': true,
    'clients:write': true,
    'documents:read': true,
    'documents:write': true,
    'tasks:read': true,
    'tasks:write': true,
    'calendar:read': true,
    'calendar:write': true,
    'time_entries:read': true,
    'time_entries:write': true,
    'billing:read': true,
    'meetings:read': true,
    'meetings:write': true,
    'calls:read': true,
    'calls:write': true,
    'emails:read': true,
    'emails:write': true,
  },
  client: {},
};

export type AuthUserLike = {
  role?: string | null;
  permissions?: string[] | Record<string, boolean> | null;
  isPlatformAdmin?: boolean;
  isOfficeManager?: boolean;
  isManagingAttorney?: boolean;
} | null | undefined;

/**
 * Mirrors backend `User.FIRM_ADMIN_DUAL_ROLE_FLAGS`. Small firms combine roles:
 * the paralegal runs the office, or the lawyer owns the firm. Adding a role here
 * is the only frontend change needed to grant it firm-admin access.
 */
export const FIRM_ADMIN_DUAL_ROLE_FLAGS: Record<string, 'isOfficeManager' | 'isManagingAttorney'> = {
  paralegal: 'isOfficeManager',
  lawyer: 'isManagingAttorney',
};

/** Human label for the combined role, used on the Team Management chip. */
export const FIRM_ADMIN_DUAL_ROLE_LABELS: Record<string, string> = {
  paralegal: 'Office Manager',
  lawyer: 'Administrator',
};

function storedPermissionValue(
  permissions: string[] | Record<string, boolean> | null | undefined,
  permission: string
): boolean | undefined {
  if (!permissions) return undefined;
  if (Array.isArray(permissions)) {
    if (permissions.includes(permission)) return true;
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(permissions, permission)) {
    return Boolean(permissions[permission]);
  }
  return undefined;
}

export function userHasRole(user: AuthUserLike, roles: string | string[]): boolean {
  if (!user?.role) return false;
  const list = Array.isArray(roles) ? roles : [roles];
  return list.includes(user.role);
}

/**
 * Firm admin: tenant admin role, or a dual-role holder — paralegal flagged as
 * office manager, lawyer flagged as managing attorney.
 */
export function isFirmAdmin(user: AuthUserLike): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const flag = FIRM_ADMIN_DUAL_ROLE_FLAGS[user.role || ''];
  return Boolean(flag && user[flag]);
}

/**
 * Matches backend User.hasPermission: admin / office-manager bypass, admin-only hard-block,
 * role ceiling (no elevation), stored false = revoke.
 */
export function userHasPermission(user: AuthUserLike, permission: string): boolean {
  if (!user) return false;
  if (isFirmAdmin(user)) return true;
  if (ADMIN_ONLY_PERMISSIONS.has(permission)) return false;

  const roleDefaults = ROLE_DEFAULT_PERMISSIONS[user.role || ''] || {};
  if (roleDefaults[permission] !== true) return false;

  const stored = storedPermissionValue(user.permissions, permission);
  if (stored === false) return false;
  return true;
}

/** Firm financial reports / AR / realization — firm admin or lawyer with billing:write. */
export function canViewFirmFinancials(user: AuthUserLike): boolean {
  if (!user) return false;
  if (isFirmAdmin(user)) return true;
  return user.role === 'lawyer' && userHasPermission(user, 'billing:write');
}

export type NavAccess =
  | { kind: 'staff' }
  | { kind: 'any' }
  | { kind: 'admin' }
  | { kind: 'platformAdmin' }
  | { kind: 'permission'; permission: string }
  | { kind: 'anyPermission'; permissions: string[] };

export function canAccessNav(user: AuthUserLike, access?: NavAccess): boolean {
  if (!user) return false;
  if (user.role === 'client') return false;
  if (!access) return false;
  switch (access.kind) {
    case 'staff':
    case 'any':
      return true;
    case 'admin':
      return isFirmAdmin(user) || Boolean(user.isPlatformAdmin);
    case 'platformAdmin':
      return Boolean(user.isPlatformAdmin);
    case 'permission':
      return userHasPermission(user, access.permission);
    case 'anyPermission':
      return access.permissions.some((p) => userHasPermission(user, p));
    default:
      return false;
  }
}

/** Path → access rule used by Layout nav and ProtectedRoute. Unknown paths deny. */
export const ROUTE_ACCESS: Record<string, NavAccess> = {
  '/': { kind: 'staff' },
  '/dashboard': { kind: 'staff' },
  '/help': { kind: 'staff' },
  '/notifications': { kind: 'staff' },
  '/profile': { kind: 'staff' },
  '/cases': { kind: 'permission', permission: 'cases:read' },
  '/clients': { kind: 'permission', permission: 'clients:read' },
  '/intake-pipeline': { kind: 'permission', permission: 'clients:read' },
  '/templates': { kind: 'permission', permission: 'documents:read' },
  '/court-forms': { kind: 'permission', permission: 'documents:read' },
  '/documents': { kind: 'permission', permission: 'documents:read' },
  '/drive': { kind: 'permission', permission: 'documents:read' },
  '/tasks': { kind: 'permission', permission: 'tasks:read' },
  '/calendar': { kind: 'permission', permission: 'calendar:read' },
  '/time-entries': { kind: 'permission', permission: 'time_entries:read' },
  '/users': { kind: 'admin' },
  '/security': { kind: 'admin' },
  '/settings': { kind: 'admin' },
  '/tenant': { kind: 'admin' },
  '/subscription': { kind: 'admin' },
  '/cms': { kind: 'admin' },
  '/growth': { kind: 'platformAdmin' },
  '/analytics': { kind: 'admin' },
  '/staff-workload': { kind: 'admin' },
  '/billing': { kind: 'permission', permission: 'billing:read' },
  '/invoices': { kind: 'permission', permission: 'billing:read' },
  '/expense-approvals': { kind: 'permission', permission: 'billing:write' },
  '/reports': { kind: 'permission', permission: 'reports:read' },
  '/report-center': { kind: 'permission', permission: 'reports:read' },
  '/portal-inbox': { kind: 'permission', permission: 'clients:read' },
  '/mail': { kind: 'permission', permission: 'emails:read' },
};

export function accessForPath(pathname: string): NavAccess | undefined {
  if (ROUTE_ACCESS[pathname]) return ROUTE_ACCESS[pathname];
  const keys = Object.keys(ROUTE_ACCESS)
    .filter((k) => k !== '/')
    .sort((a, b) => b.length - a.length);
  const prefix = keys.find((k) => pathname === k || pathname.startsWith(`${k}/`));
  return prefix ? ROUTE_ACCESS[prefix] : undefined;
}

export function canAccessRoute(user: AuthUserLike, path: string): boolean {
  return canAccessNav(user, accessForPath(path));
}

export const TEAM_PERMISSION_OPTIONS = [
  { value: 'cases:read', label: 'View Cases' },
  { value: 'cases:write', label: 'Create/Edit Cases' },
  { value: 'cases:delete', label: 'Delete Cases' },
  { value: 'cases:assign', label: 'Assign Cases' },
  { value: 'clients:read', label: 'View Clients' },
  { value: 'clients:write', label: 'Create/Edit Clients' },
  { value: 'clients:delete', label: 'Delete Clients' },
  { value: 'documents:read', label: 'View Documents' },
  { value: 'documents:write', label: 'Create/Edit Documents' },
  { value: 'documents:delete', label: 'Delete Documents' },
  { value: 'billing:read', label: 'View Billing' },
  { value: 'billing:write', label: 'Create/Edit Billing' },
  { value: 'billing:delete', label: 'Delete Billing / Invoices' },
  { value: 'reports:read', label: 'View Reports' },
  { value: 'trust:write', label: 'Update Trust Balances' },
  { value: 'portal:admin', label: 'Manage Client Portal Access' },
  { value: 'emails:read', label: 'View Sent Mail' },
  { value: 'emails:write', label: 'Compose & Send Mail' },
  // Admin-only keys (users:*, settings:*) intentionally omitted — runtime hard-blocks them
] as const;
